"use client";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { IJob } from "@globalTypes";
import { applyToJob, unApplyJob } from "@components/Jobs/JobDetails/actions";
import { useRouter } from "next/navigation";

interface JobContextType {
  job: IJob;
  isApplied: boolean;
  applyForJob: () => Promise<boolean>;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

interface JobProviderProps {
  data: IJob;
  alreadyApplied: boolean;
  children: ReactNode;
}

export const JobProvider = ({
  children,
  data,
  alreadyApplied,
}: JobProviderProps) => {
  const router = useRouter();

  const [isApplied, setIsApplied] = useState<boolean>(alreadyApplied);

  const applyForJob = async () => {
    try {
      if (isApplied) {
        await unApplyJob(data.id, data.slug);
        setIsApplied(false);
      } else {
        await applyToJob(data.id, data.slug);
        setIsApplied(true);
      }

      router.refresh();
      return true;
    } catch (error) {
      return false;
    }
  };

  if (!data) return null;

  return (
    <JobContext.Provider
      value={{
        job: data,
        isApplied,
        applyForJob,
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJob = () => {
  const context = useContext(JobContext);
  if (!context) throw new Error("useJob must be used within a JobProvider");
  return context;
};
